// State + write actions behind the OTA admin panel, kept out of the presentational
// components so each of those stays small. One subscription to the live `config/ota`
// doc; three write paths (save the gate flags, promote/roll back to a bundle) and one
// read-only health check (verify). All writes go through the password-gated
// `setOtaConfig` callable, which preserves the upload history server-side.
import { useEffect, useRef, useState } from 'react';
import type { OtaBundleRef, OtaConfig } from '@h2h/shared';
import { useOtaConfig } from '../../../hooks/useQueries';
import {
  isWrongPasswordError,
  setOtaConfig,
  verifyOtaBundle,
  type VerifyOtaBundleResult
} from '../../../lib/adminApi';
import { useAdminData } from '../parts';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';
type VerifyState = 'idle' | 'checking' | 'done' | 'error';

function describeError(err: unknown, fallback: string): string {
  return isWrongPasswordError(err)
    ? 'Admin password rejected (it may have rotated). Lock and sign in again.'
    : (err as { message?: string })?.message || fallback;
}

export function useOtaAdmin() {
  const { password } = useAdminData();
  const { data: stored } = useOtaConfig();

  const [enabled, setEnabled] = useState(false);
  const [mandatory, setMandatory] = useState(false);
  const [minNativeBuild, setMinNativeBuild] = useState('');
  const [notes, setNotes] = useState('');

  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [promotingVersion, setPromotingVersion] = useState<string | null>(null);

  const [verifyState, setVerifyState] = useState<VerifyState>('idle');
  const [verifyResult, setVerifyResult] = useState<VerifyOtaBundleResult | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Newest upload first — the history the admin rolls back/forward through.
  const available = [...(stored?.available ?? [])].sort((a, b) => b.at - a.at);

  // Seed the gate form from the stored doc once it first loads.
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || stored === undefined) return;
    setEnabled(!!stored?.enabled);
    setMandatory(!!stored?.mandatory);
    setMinNativeBuild(stored?.minNativeBuild != null ? String(stored.minNativeBuild) : '');
    setNotes(stored?.notes ?? '');
    hydrated.current = true;
  }, [stored]);

  const liveVersion = stored?.version ?? '';
  const hasLiveBundle = !!(stored?.version && stored?.url);
  // Can't arm the gate without a real, uploaded live bundle behind it.
  const blocked = enabled && !hasLiveBundle;

  /** Save the gate flags, keeping the current live pointer + history intact. */
  async function save() {
    if (saveState === 'saving' || blocked) return;
    setSaveState('saving');
    setSaveError(null);
    try {
      const cfg: OtaConfig = {
        enabled,
        version: stored?.version,
        url: stored?.url,
        checksum: stored?.checksum,
        minNativeBuild: minNativeBuild.trim() ? Number(minNativeBuild) : stored?.minNativeBuild,
        mandatory: mandatory || undefined,
        notes: notes.trim() || undefined
      };
      await setOtaConfig(password, cfg);
      setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (e) {
      setSaveState('error');
      setSaveError(describeError(e, 'Could not save. Check the setOtaConfig logs.'));
    }
  }

  /**
   * Point the live release at an uploaded bundle (the rollback/forward lever).
   * An immediate, standalone write — it deliberately keeps the CURRENTLY SAVED
   * gate state (enabled/mandatory/notes) rather than the unsaved form, so this
   * never silently flips the kill switch. It carries the bundle's own native floor.
   */
  async function promote(ref: OtaBundleRef) {
    if (promotingVersion) return;
    setPromotingVersion(ref.version);
    setSaveError(null);
    try {
      const cfg: OtaConfig = {
        enabled: stored?.enabled ?? false,
        version: ref.version,
        url: ref.url,
        checksum: ref.checksum,
        minNativeBuild: ref.minNativeBuild,
        mandatory: stored?.mandatory || undefined,
        notes: stored?.notes || undefined
      };
      await setOtaConfig(password, cfg);
    } catch (e) {
      setSaveError(describeError(e, 'Could not set live. Check the setOtaConfig logs.'));
    } finally {
      setPromotingVersion(null);
    }
  }

  /** Fetch the live (or a named) bundle server-side and re-check it. Read-only. */
  async function verify(version?: string) {
    if (verifyState === 'checking') return;
    setVerifyState('checking');
    setVerifyError(null);
    setVerifyResult(null);
    try {
      const result = await verifyOtaBundle(password, version);
      setVerifyResult(result);
      setVerifyState('done');
    } catch (e) {
      setVerifyState('error');
      setVerifyError(describeError(e, 'Verify failed. Check the verifyOtaBundle logs.'));
    }
  }

  return {
    stored,
    available,
    liveVersion,
    hasLiveBundle,
    blocked,
    enabled,
    setEnabled,
    mandatory,
    setMandatory,
    minNativeBuild,
    setMinNativeBuild,
    notes,
    setNotes,
    save,
    saveState,
    saveError,
    promote,
    promotingVersion,
    verify,
    verifyState,
    verifyResult,
    verifyError
  };
}

export type OtaAdmin = ReturnType<typeof useOtaAdmin>;
